"use client";

import { Children, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { attrsToProps, cx } from "../render/attrs";

type Header = { attrs: Record<string, string>; title: string; targetId: string };

type Props = {
  mainAttrs: Record<string, string>;
  navigationAttrs?: Record<string, string>;
  contentAttrs?: Record<string, string>;
  headers: Header[];
  activeTab: number;
  /** Rendered `tab-item` content types, in header order. */
  children?: ReactNode;
};

/**
 * Page Builder Tabs (Magento: jQuery UI tabs). Every panel is rendered on the
 * server (SEO, no-JS), inactive ones carry `hidden`. Implements the WAI-ARIA
 * tabs pattern (roving tabindex, arrow/Home/End keys) and the native
 * negative-margin trick that merges the active header into the content border.
 */
export function PbTabs({ mainAttrs, navigationAttrs, contentAttrs, headers, activeTab, children }: Props) {
  const panels = Children.toArray(children);
  const count = Math.max(headers.length, panels.length);
  const [active, setActive] = useState(Math.min(Math.max(0, activeTab), Math.max(0, count - 1)));
  const navRef = useRef<HTMLUListElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Array<HTMLAnchorElement | null>>([]);

  // jQuery UI `create` hook parity: overlap header and content borders.
  useEffect(() => {
    const nav = navRef.current;
    const content = contentRef.current;
    if (!nav || !content) return;
    const border = Number.parseFloat(getComputedStyle(content).borderTopWidth) || 0;
    nav.style.marginBottom = `-${border}px`;
    nav.querySelectorAll<HTMLElement>("li:not(:first-child)").forEach((li) => {
      li.style.marginLeft = `-${border}px`;
    });
  }, []);

  const select = (index: number, focus = false) => {
    const next = (index + count) % count;
    setActive(next);
    if (focus) tabRefs.current[next]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLAnchorElement>, index: number) => {
    const keys: Record<string, number> = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: count - 1 };
    if (!(e.key in keys)) return;
    e.preventDefault();
    select(keys[e.key], true);
  };

  const idFor = (i: number) => headers[i]?.targetId || `${mainAttrs["data-pb-style"] ?? "pb-tabs"}-${i}`;

  return (
    <div {...attrsToProps(mainAttrs, "pb-tabs")} data-pb-initialized="">
      <ul {...attrsToProps(navigationAttrs, "tabs-navigation")} ref={navRef} role="tablist">
        {headers.map((header, i) => (
          <li
            key={i}
            {...attrsToProps(header.attrs, cx("tab-header", i === active && "ui-state-active ui-tabs-active is-active"))}
            role="presentation"
          >
            <a
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              href={`#${idFor(i)}`}
              className="tab-title"
              role="tab"
              id={`${idFor(i)}-tab`}
              aria-controls={idFor(i)}
              aria-selected={i === active}
              tabIndex={i === active ? 0 : -1}
              data-pb-native=""
              onClick={(e) => {
                e.preventDefault();
                select(i);
              }}
              onKeyDown={(e) => onKeyDown(e, i)}
            >
              <span className="tab-title">{header.title}</span>
            </a>
          </li>
        ))}
      </ul>
      <div {...attrsToProps(contentAttrs, "tabs-content")} ref={contentRef}>
        {panels.map((panel, i) => (
          <div
            key={i}
            className="pb-tab-panel"
            role="tabpanel"
            aria-labelledby={`${idFor(i)}-tab`}
            hidden={i !== active}
          >
            {panel}
          </div>
        ))}
      </div>
    </div>
  );
}
